import React, { useState, useEffect } from 'react';
import { getTenantToday } from '../../utils/dateUtils';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity,
  AlertTriangle,
  Shield,
  TrendingUp,
  TrendingDown,
  RefreshCcw,
  PieChart,
  BarChart3,
  Target,
  Zap,
  ChevronRight,
  Info,
  CheckCircle2,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { cn } from '../../lib/utils';

const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

interface PortfolioHealth {
  portfolioId: number;
  portfolioName: string;
  accountType: string;
  totalValue: number;
  positionCount: number;
  riskScore: number;
  riskLevel: string;
  maxDrift: number;
  hasDriftAlert: boolean;
  maxConcentration: number;
  hasConcentrationAlert: boolean;
  healthStatus: string;
}

interface DashboardData {
  portfolioCount: number;
  averageRiskScore: number;
  driftAlerts: number;
  concentrationAlerts: number;
  portfolios: PortfolioHealth[];
  asOfDate: string;
}

interface AllocationDrift {
  assetClass: string;
  targetWeight: number;
  currentWeight: number;
  drift: number;
  needsRebalance: boolean;
}

interface WhatIfResult {
  scenarioType: string;
  currentValue: number;
  projectedValue: number;
  totalImpact: number;
  totalImpactPercent: number;
  impacts: Array<{
    assetClass: string;
    currentValue: number;
    projectedValue: number;
    impact: number;
    impactPercent: number;
  }>;
}

const PortfolioHealthScreen: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState<number | null>(null);
  const [driftData, setDriftData] = useState<{ allocations: AllocationDrift[] } | null>(null);
  const [whatIfResult, setWhatIfResult] = useState<WhatIfResult | null>(null);
  const [whatIfScenario, setWhatIfScenario] = useState({ type: 'MARKET_DROP', changePercent: -20 });
  const [activeTab, setActiveTab] = useState<'overview' | 'drift' | 'concentration' | 'whatif'>('overview');

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    if (selectedPortfolio) {
      fetchDriftData(selectedPortfolio);
    }
  }, [selectedPortfolio]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/portfolio-health/dashboard');
      setDashboard(response.data);
      if (response.data.portfolios?.length > 0 && !selectedPortfolio) {
        setSelectedPortfolio(response.data.portfolios[0].portfolioId);
      }
    } catch (error) {
      console.error('Failed to fetch portfolio health:', error);
      // Set empty state - no mock data
      setDashboard({
        portfolioCount: 0,
        averageRiskScore: 0,
        driftAlerts: 0,
        concentrationAlerts: 0,
        asOfDate: getTenantToday(),
        portfolios: []
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDriftData = async (portfolioId: number) => {
    try {
      const response = await axios.get(`/api/portfolio-health/portfolio/${portfolioId}/drift`);
      setDriftData(response.data);
    } catch (error) {
      console.error('Failed to fetch drift data:', error);
      setDriftData({ allocations: [] });
    }
  };

  const runWhatIf = async () => {
    if (!selectedPortfolio) return;
    try {
      const response = await axios.post(`/api/portfolio-health/portfolio/${selectedPortfolio}/what-if`, whatIfScenario);
      setWhatIfResult(response.data);
    } catch (error) {
      console.error('Failed to run what-if scenario:', error);
      setWhatIfResult(null);
    }
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
  };

  const formatPercent = (val: number, showSign = true) => {
    const sign = showSign && val >= 0 ? '+' : '';
    return `${sign}${val.toFixed(1)}%`;
  };

  const getRiskScoreColor = (score: number) => {
    if (score <= 3) return 'text-emerald-400';
    if (score <= 5) return 'text-blue-400';
    if (score <= 7) return 'text-amber-400';
    return 'text-red-400';
  };

  const getHealthStatusIcon = (status: string) => {
    switch (status) {
      case 'HEALTHY': return <CheckCircle2 className="w-4 h-4 text-emerald-400" />;
      case 'GOOD': return <CheckCircle2 className="w-4 h-4 text-blue-400" />;
      case 'MONITOR': return <AlertCircle className="w-4 h-4 text-amber-400" />;
      case 'NEEDS_ATTENTION': return <XCircle className="w-4 h-4 text-red-400" />;
      default: return <Info className="w-4 h-4 text-slate-400" />;
    }
  };

  const selectedPortfolioData = dashboard?.portfolios.find(p => p.portfolioId === selectedPortfolio);

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-end"
        >
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <Activity className="w-8 h-8 text-emerald-500" />
              Portfolio Health
              <span className="ml-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-xs font-bold text-emerald-400 uppercase tracking-widest">
                RIA
              </span>
            </h1>
            <p className="text-slate-500 font-medium mt-1">
              Risk scores, allocation drift, and concentration analysis
            </p>
          </div>
          <button
            onClick={fetchDashboard}
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
          >
            <RefreshCcw className={cn("w-4 h-4", loading && "animate-spin")} />
            Refresh
          </button>
        </motion.div>

        {/* Summary Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <GlassCard className="p-6 border-l-4 border-l-blue-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Portfolios</div>
                <div className="text-2xl font-bold text-white tracking-tight">{dashboard?.portfolioCount || 0}</div>
                <div className="text-[10px] text-slate-500 mt-2">Under management</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400">
                <PieChart className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6 border-l-4 border-l-emerald-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Avg Risk Score</div>
                <div className={cn("text-2xl font-bold tracking-tight", getRiskScoreColor(dashboard?.averageRiskScore || 5))}>
                  {dashboard?.averageRiskScore || 5} / 10
                </div>
                <div className="text-[10px] text-slate-500 mt-2">Across all portfolios</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                <Shield className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6 border-l-4 border-l-amber-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Drift Alerts</div>
                <div className={cn("text-2xl font-bold tracking-tight", (dashboard?.driftAlerts || 0) > 0 ? "text-amber-400" : "text-emerald-400")}>
                  {dashboard?.driftAlerts || 0}
                </div>
                <div className="text-[10px] text-slate-500 mt-2">Need rebalancing</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center text-amber-400">
                <Target className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6 border-l-4 border-l-red-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Concentration Alerts</div>
                <div className={cn("text-2xl font-bold tracking-tight", (dashboard?.concentrationAlerts || 0) > 0 ? "text-red-400" : "text-emerald-400")}>
                  {dashboard?.concentrationAlerts || 0}
                </div>
                <div className="text-[10px] text-slate-500 mt-2">Limit breaches</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center text-red-400">
                <AlertTriangle className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Portfolio List */}
          <GlassCard className="lg:col-span-1">
            <div className="p-4 border-b border-white/5 bg-slate-950/30">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Activity className="w-4 h-4 text-emerald-400" />
                Portfolio Health
              </h3>
            </div>
            <div className="divide-y divide-white/5">
              {dashboard?.portfolios.map((portfolio) => (
                <button
                  key={portfolio.portfolioId}
                  onClick={() => setSelectedPortfolio(portfolio.portfolioId)}
                  className={cn(
                    "w-full p-4 text-left transition-all",
                    selectedPortfolio === portfolio.portfolioId
                      ? "bg-emerald-500/10 border-l-2 border-l-emerald-500"
                      : "hover:bg-white/5"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-white text-sm">{portfolio.portfolioName}</div>
                    {getHealthStatusIcon(portfolio.healthStatus)}
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-slate-500">Risk: <span className={getRiskScoreColor(portfolio.riskScore)}>{portfolio.riskScore}/10</span></span>
                    <span className={cn("text-xs font-bold", portfolio.hasDriftAlert ? "text-amber-400" : "text-slate-500")}>
                      {portfolio.maxDrift.toFixed(1)}% drift
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </GlassCard>

          {/* Detail Panel */}
          <div className="lg:col-span-3 space-y-6">
            {/* Tabs */}
            <div className="flex bg-slate-900 border border-white/10 p-1 rounded-xl w-fit">
              {[
                { id: 'overview', label: 'Overview', icon: Activity },
                { id: 'drift', label: 'Allocation Drift', icon: Target },
                { id: 'concentration', label: 'Concentration', icon: PieChart },
                { id: 'whatif', label: 'What-If', icon: Zap }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={cn(
                    "px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2",
                    activeTab === tab.id ? "bg-emerald-600 text-white shadow-lg" : "text-slate-500 hover:text-slate-300"
                  )}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
              {activeTab === 'overview' && selectedPortfolioData && (
                <motion.div
                  key="overview"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <GlassCard>
                    <div className="p-4 border-b border-white/5 bg-slate-950/30">
                      <h3 className="text-lg font-bold text-white">{selectedPortfolioData.portfolioName}</h3>
                      <span className="text-xs text-slate-500">{selectedPortfolioData.accountType} Account</span>
                    </div>
                    <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Risk Score Gauge */}
                      <div className="text-center">
                        <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">Risk Score</div>
                        <div className={cn("text-6xl font-bold", getRiskScoreColor(selectedPortfolioData.riskScore))}>
                          {selectedPortfolioData.riskScore}
                        </div>
                        <div className="text-sm text-slate-400 mt-2">out of 10</div>
                        <div className={cn("mt-2 px-3 py-1 rounded-full text-xs font-bold inline-block",
                          selectedPortfolioData.riskLevel === 'LOW' ? "bg-emerald-500/20 text-emerald-400" :
                          selectedPortfolioData.riskLevel === 'MODERATE' ? "bg-blue-500/20 text-blue-400" :
                          selectedPortfolioData.riskLevel === 'ELEVATED' ? "bg-amber-500/20 text-amber-400" :
                          "bg-red-500/20 text-red-400"
                        )}>
                          {selectedPortfolioData.riskLevel}
                        </div>
                      </div>

                      {/* Portfolio Stats */}
                      <div className="space-y-4">
                        <div>
                          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Total Value</div>
                          <div className="text-xl font-bold text-white">{formatCurrency(selectedPortfolioData.totalValue)}</div>
                        </div>
                        <div>
                          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Positions</div>
                          <div className="text-xl font-bold text-white">{selectedPortfolioData.positionCount}</div>
                        </div>
                      </div>

                      {/* Alerts */}
                      <div className="space-y-4">
                        <div className={cn("p-3 rounded-lg border", selectedPortfolioData.hasDriftAlert ? "bg-amber-500/10 border-amber-500/20" : "bg-emerald-500/10 border-emerald-500/20")}>
                          <div className="flex items-center gap-2">
                            {selectedPortfolioData.hasDriftAlert ? <AlertTriangle className="w-4 h-4 text-amber-400" /> : <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                            <span className={cn("text-sm font-bold", selectedPortfolioData.hasDriftAlert ? "text-amber-400" : "text-emerald-400")}>
                              {selectedPortfolioData.hasDriftAlert ? 'Drift Alert' : 'Within Target'}
                            </span>
                          </div>
                          <div className="text-xs text-slate-400 mt-1">Max drift: {selectedPortfolioData.maxDrift.toFixed(1)}%</div>
                        </div>

                        <div className={cn("p-3 rounded-lg border", selectedPortfolioData.hasConcentrationAlert ? "bg-red-500/10 border-red-500/20" : "bg-emerald-500/10 border-emerald-500/20")}>
                          <div className="flex items-center gap-2">
                            {selectedPortfolioData.hasConcentrationAlert ? <XCircle className="w-4 h-4 text-red-400" /> : <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                            <span className={cn("text-sm font-bold", selectedPortfolioData.hasConcentrationAlert ? "text-red-400" : "text-emerald-400")}>
                              {selectedPortfolioData.hasConcentrationAlert ? 'Concentration Alert' : 'Diversified'}
                            </span>
                          </div>
                          <div className="text-xs text-slate-400 mt-1">Max position: {selectedPortfolioData.maxConcentration.toFixed(1)}%</div>
                        </div>
                      </div>
                    </div>
                  </GlassCard>
                </motion.div>
              )}

              {activeTab === 'drift' && driftData && (
                <motion.div
                  key="drift"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <GlassCard>
                    <div className="p-4 border-b border-white/5 bg-slate-950/30">
                      <h3 className="text-lg font-bold text-white">Allocation Drift Analysis</h3>
                      <span className="text-xs text-slate-500">Current vs Target allocation</span>
                    </div>
                    <div className="p-6">
                      <table className="w-full">
                        <thead>
                          <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                            <th className="py-3 text-left">Asset Class</th>
                            <th className="py-3 text-right">Target</th>
                            <th className="py-3 text-right">Current</th>
                            <th className="py-3 text-right">Drift</th>
                            <th className="py-3 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {driftData.allocations.map((alloc) => (
                            <tr key={alloc.assetClass} className="border-b border-white/5 last:border-0">
                              <td className="py-4 font-bold text-white">{alloc.assetClass}</td>
                              <td className="py-4 text-right text-slate-400">{alloc.targetWeight}%</td>
                              <td className="py-4 text-right text-white font-mono">{alloc.currentWeight.toFixed(1)}%</td>
                              <td className={cn("py-4 text-right font-bold font-mono", alloc.drift >= 0 ? "text-emerald-400" : "text-red-400")}>
                                {formatPercent(alloc.drift)}
                              </td>
                              <td className="py-4 text-center">
                                {alloc.needsRebalance ? (
                                  <span className="px-2 py-1 bg-amber-500/10 text-amber-400 text-xs font-bold rounded">REBALANCE</span>
                                ) : (
                                  <span className="px-2 py-1 bg-emerald-500/10 text-emerald-400 text-xs font-bold rounded">OK</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </GlassCard>
                </motion.div>
              )}

              {activeTab === 'whatif' && (
                <motion.div
                  key="whatif"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                >
                  <GlassCard>
                    <div className="p-4 border-b border-white/5 bg-slate-950/30">
                      <h3 className="text-lg font-bold text-white">What-If Scenario</h3>
                      <span className="text-xs text-slate-500">Simulate market impacts on your portfolio</span>
                    </div>
                    <div className="p-6">
                      <div className="flex gap-4 items-end">
                        <div className="flex-1">
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">Scenario</label>
                          <select
                            value={whatIfScenario.type}
                            onChange={(e) => setWhatIfScenario({ ...whatIfScenario, type: e.target.value })}
                            className="w-full px-4 py-2 bg-slate-900 border border-white/10 rounded-lg text-white"
                          >
                            <option value="MARKET_DROP">Market Drop</option>
                            <option value="MARKET_RISE">Market Rise</option>
                            <option value="RATE_SHOCK">Interest Rate Shock</option>
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">Change %</label>
                          <input
                            type="number"
                            value={whatIfScenario.changePercent}
                            onChange={(e) => setWhatIfScenario({ ...whatIfScenario, changePercent: parseFloat(e.target.value) })}
                            className="w-full px-4 py-2 bg-slate-900 border border-white/10 rounded-lg text-white"
                          />
                        </div>
                        <button
                          onClick={runWhatIf}
                          className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-lg transition-all flex items-center gap-2"
                        >
                          <Zap className="w-4 h-4" />
                          Run Scenario
                        </button>
                      </div>
                    </div>
                  </GlassCard>

                  {whatIfResult && (
                    <GlassCard>
                      <div className="p-4 border-b border-white/5 bg-slate-950/30">
                        <h3 className="text-lg font-bold text-white">Scenario Results</h3>
                      </div>
                      <div className="p-6">
                        <div className="grid grid-cols-3 gap-6 mb-6">
                          <div>
                            <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Current Value</div>
                            <div className="text-xl font-bold text-white">{formatCurrency(whatIfResult.currentValue)}</div>
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Projected Value</div>
                            <div className={cn("text-xl font-bold", whatIfResult.totalImpact >= 0 ? "text-emerald-400" : "text-red-400")}>
                              {formatCurrency(whatIfResult.projectedValue)}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Total Impact</div>
                            <div className={cn("text-xl font-bold", whatIfResult.totalImpact >= 0 ? "text-emerald-400" : "text-red-400")}>
                              {formatCurrency(whatIfResult.totalImpact)} ({formatPercent(whatIfResult.totalImpactPercent)})
                            </div>
                          </div>
                        </div>

                        <table className="w-full">
                          <thead>
                            <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                              <th className="py-3 text-left">Asset Class</th>
                              <th className="py-3 text-right">Current</th>
                              <th className="py-3 text-right">Projected</th>
                              <th className="py-3 text-right">Impact</th>
                            </tr>
                          </thead>
                          <tbody>
                            {whatIfResult.impacts.map((impact) => (
                              <tr key={impact.assetClass} className="border-b border-white/5 last:border-0">
                                <td className="py-4 font-bold text-white">{impact.assetClass}</td>
                                <td className="py-4 text-right text-slate-400 font-mono">{formatCurrency(impact.currentValue)}</td>
                                <td className="py-4 text-right text-white font-mono">{formatCurrency(impact.projectedValue)}</td>
                                <td className={cn("py-4 text-right font-bold font-mono", impact.impact >= 0 ? "text-emerald-400" : "text-red-400")}>
                                  {formatCurrency(impact.impact)} ({formatPercent(impact.impactPercent)})
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </GlassCard>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default PortfolioHealthScreen;
